import React, { useState } from 'react';
import { forwardTrace } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, Loader2, FastForward, ArrowDown, FlaskConical, PackageOpen, Truck, ShoppingCart, User, Factory } from 'lucide-react';

const SEARCH_TYPES = [
  { value: 'milk_intake_ref', label: 'Milk intake reference', placeholder: 'e.g. MI-2026-00001' },
  { value: 'supplier_lot', label: 'Supplier lot number', placeholder: 'e.g. MA4001-26A007' },
  { value: 'make_day_ref', label: 'Make Day reference', placeholder: 'e.g. MD-2026-00001' },
  { value: 'cheese_batch_number', label: 'Cheese batch number', placeholder: 'e.g. CB-2026-014' },
];

export default function ForwardTrace() {
  const [searchType, setSearchType] = useState('milk_intake_ref');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!value.trim()) { toast.error('Enter a value to search'); return; }
    setLoading(true);
    try {
      const r = await forwardTrace({ [searchType]: value.trim() });
      setResult(r.data);
      if (!r.data.cheese_batches?.length && !r.data.make_days?.length) {
        toast.info('No forward chain found. Has this been used on a make day yet?');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Trace failed'); }
    finally { setLoading(false); }
  };

  const cur = SEARCH_TYPES.find(t => t.value === searchType);

  return (
    <div className="space-y-4" data-testid="forward-trace-page">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2"><FastForward className="w-5 h-5" />Forward Trace</h1>
        <p className="text-sm text-muted-foreground">Start with a supplier lot or milk intake and see every make day, batch, dispatch and customer it ended up in. Critical for mock recalls and audits.</p>
      </div>

      <Card><CardContent className="pt-4">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label>Search by</Label>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger data-testid="ftrace-search-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SEARCH_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex gap-2">
            <div className="flex-1">
              <Label>Value</Label>
              <Input value={value} onChange={e => setValue(e.target.value)} placeholder={cur?.placeholder} onKeyDown={e => e.key === 'Enter' && run()} data-testid="ftrace-value-input" />
            </div>
            <Button onClick={run} disabled={loading} className="self-end" data-testid="btn-run-ftrace">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}Trace
            </Button>
          </div>
        </div>
      </CardContent></Card>

      {result && <Card data-testid="ftrace-result"><CardContent className="pt-6">
        <div className="text-sm text-muted-foreground mb-1">Starting point</div>
        <div className="text-xl font-semibold mb-6">{result.starting || '—'}</div>

        <div className="relative pl-8">
          <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-zinc-200" />

          {result.make_days?.length > 0 && <Node icon={FlaskConical} title="Make Days" testid="ftrace-make-days" accent>
            <div className="space-y-1">
              {result.make_days.map((m, i) => (
                <div key={i} className="text-sm"><span className="font-mono font-semibold">{m.make_day_ref}</span> <span className="text-muted-foreground">· {m.product_type} · {m.make_date}</span></div>
              ))}
            </div>
          </Node>}

          {result.cheese_batches?.length > 0 && <Node icon={PackageOpen} title="Cheese batches produced" testid="ftrace-batches">
            <div className="flex flex-wrap gap-2">
              {result.cheese_batches.map((b, i) => (
                <div key={i} className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded">
                  <div className="font-mono text-sm font-semibold">{b.batch_number || b.batch_code}</div>
                  {b.use_by_date && <div className="text-xs text-muted-foreground">Use-by {b.use_by_date}</div>}
                </div>
              ))}
            </div>
          </Node>}

          {result.downstream?.length > 0 ? result.downstream.map((row, i) => (
            <Node key={i} icon={Truck} title={`Dispatch → Customer`} testid={`ftrace-downstream-${i}`}>
              <div className="text-sm">
                <div><span className="font-mono">{row.dispatch?.reference || row.dispatch?.dispatch_ref || row.dispatch?.id?.slice(0, 8)}</span> · {row.dispatch?.dispatch_date || ''}</div>
                <div className="text-muted-foreground mt-0.5"><User className="inline w-3 h-3 mr-1" />{row.customer?.name || '—'}</div>
                {row.sales_orders?.length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1"><ShoppingCart className="inline w-3 h-3 mr-1" />Sales orders: {row.sales_orders.map(so => so.order_number).join(', ')}</div>
                )}
              </div>
            </Node>
          )) : result.cheese_batches?.length > 0 && (
            <Node icon={Truck} title="Downstream dispatches" testid="ftrace-no-downstream">
              <div className="text-sm text-muted-foreground italic">No dispatches found yet for these batches. They haven't shipped.</div>
            </Node>
          )}

          {(!result.make_days?.length && !result.cheese_batches?.length) &&
            <div className="ml-2 text-sm text-muted-foreground italic py-8">No forward chain found. This intake/lot may not have been used on a make day yet.</div>}
        </div>
      </CardContent></Card>}
    </div>
  );
}

function Node({ icon: Icon, title, children, testid, accent }) {
  return (
    <div className="relative pb-5" data-testid={testid}>
      <div className={`absolute -left-6 top-1 w-5 h-5 rounded-full flex items-center justify-center ${accent ? 'bg-pink-600 text-white' : 'bg-white border-2 border-zinc-300 text-zinc-600'}`}>
        <Icon className="w-3 h-3" />
      </div>
      <div className={`rounded-md p-3 ${accent ? 'bg-pink-50 border border-pink-200' : 'bg-zinc-50 border border-zinc-200'}`}>
        <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">{title}</div>
        {children}
      </div>
      <ArrowDown className="absolute -left-[14px] top-7 w-3 h-3 text-zinc-300" />
    </div>
  );
}
